(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-mbb-reviews]';
  const VIEWPORT_SELECTOR = '[data-mbb-reviews-viewport]';
  const TRACK_SELECTOR = '[data-mbb-reviews-track]';
  const CARD_SELECTOR = '[data-mbb-review-card]';
  const PREV_SELECTOR = '[data-mbb-reviews-prev]';
  const NEXT_SELECTOR = '[data-mbb-reviews-next]';
  const REVEAL_SELECTOR = '[data-mbb-reveal]';
  const instances = new WeakMap();

  class MbbHomeReviews {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector(VIEWPORT_SELECTOR);
      this.track = section.querySelector(TRACK_SELECTOR);
      this.cards = [...section.querySelectorAll(CARD_SELECTOR)];
      this.previousButton = section.querySelector(PREV_SELECTOR);
      this.nextButton = section.querySelector(NEXT_SELECTOR);
      this.currentIndex = 0;
      this.visibleCards = 1;
      this.gap = 0;
      this.resizeTimer = null;

      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleScroll = this.handleScroll.bind(this);

      this.init();
    }

    init() {
      this.initReveal();

      if (!this.viewport || !this.track || !this.cards.length) return;

      this.bindEvents();
      this.updateLayout();
    }

    initReveal() {
      const elements = [...this.section.querySelectorAll(REVEAL_SELECTOR)];

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        !('IntersectionObserver' in window)
      ) {
        elements.forEach((element) => {
          element.classList.add('is-visible');
        });

        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            this.observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: '0px 0px -40px'
        }
      );

      elements.forEach((element, index) => {
        if (!element.style.getPropertyValue('--mbb-delay')) {
          element.style.setProperty('--mbb-delay', `${index * 70}ms`);
        }

        this.observer.observe(element);
      });
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);

      this.viewport.addEventListener('scroll', this.handleScroll, {
        passive: true
      });

      window.addEventListener('resize', this.handleResize);
    }

    getVisibleCards() {
      if (window.innerWidth <= 749) return 1;
      if (window.innerWidth <= 990) return 2;
      if (window.innerWidth <= 1100) return 3;

      return 4;
    }

    updateLayout() {
      this.visibleCards = this.getVisibleCards();
      this.gap = parseFloat(getComputedStyle(this.track).gap) || 0;

      const maxIndex = Math.max(0, this.cards.length - this.visibleCards);

      this.currentIndex = Math.min(this.currentIndex, maxIndex);
      this.updatePosition();
    }

    updatePosition() {
      if (window.innerWidth <= 749) {
        this.track.style.transform = '';
        this.updateControls();
        return;
      }

      const cardWidth = this.cards[0]?.getBoundingClientRect().width || 0;
      const offset = this.currentIndex * (cardWidth + this.gap);

      this.track.style.transform = `translate3d(-${offset}px,0,0)`;
      this.updateControls();
    }

    updateControls() {
      const maxIndex = Math.max(0, this.cards.length - this.visibleCards);

      if (this.previousButton) {
        this.previousButton.disabled = this.currentIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = this.currentIndex >= maxIndex;
      }
    }

    handlePrevious() {
      if (this.currentIndex <= 0) return;

      this.currentIndex -= 1;
      this.updatePosition();
    }

    handleNext() {
      const maxIndex = Math.max(0, this.cards.length - this.visibleCards);

      if (this.currentIndex >= maxIndex) return;

      this.currentIndex += 1;
      this.updatePosition();
    }

    handleScroll() {
      if (window.innerWidth > 749 || !this.cards.length) return;

      const cardWidth =
        this.cards[0].getBoundingClientRect().width + this.gap;

      if (!cardWidth) return;

      this.currentIndex = Math.round(
        this.viewport.scrollLeft / cardWidth
      );

      this.updateControls();
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.updateLayout();
      }, 150);
    }

    destroy() {
      this.previousButton?.removeEventListener(
        'click',
        this.handlePrevious
      );

      this.nextButton?.removeEventListener(
        'click',
        this.handleNext
      );

      this.viewport?.removeEventListener(
        'scroll',
        this.handleScroll
      );

      window.removeEventListener('resize', this.handleResize);

      this.observer?.disconnect();
      window.clearTimeout(this.resizeTimer);

      instances.delete(this.section);
    }
  }

  function initSection(section) {
    if (!section || instances.has(section)) return;

    instances.set(section, new MbbHomeReviews(section));
  }

  function initAll(scope = document) {
    if (scope.matches?.(SECTION_SELECTOR)) {
      initSection(scope);
    }

    scope
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initSection);
  }

  function destroySection(section) {
    instances.get(section)?.destroy();
  }

  function boot() {
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, {
      once: true
    });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section =
      event.target.matches?.(SECTION_SELECTOR)
        ? event.target
        : event.target.querySelector?.(SECTION_SELECTOR);

    if (section) {
      destroySection(section);
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    event.target
      .closest(REVEAL_SELECTOR)
      ?.classList.add('is-visible');
  });
})();